import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Eraser } from 'lucide-react';

export const SignaturePad = forwardRef(({ testId = 'signature-pad', label = 'Sign in the box below *' }, ref) => {
  const canvasRef = useRef(null);
  const clear = () => { const c = canvasRef.current; c?.getContext('2d').clearRect(0, 0, c.width, c.height); };

  useImperativeHandle(ref, () => ({
    clear,
    isBlank: () => {
      const c = canvasRef.current;
      if (!c) return true;
      const { data } = c.getContext('2d').getImageData(0, 0, c.width, c.height);
      for (let i = 3; i < data.length; i += 4) if (data[i] !== 0) return false;
      return true;
    },
    toDataURL: () => canvasRef.current?.toDataURL('image/png'),
  }));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.strokeStyle = '#0f172a';
    let drawing = false;
    const pos = (e) => {
      const r = canvas.getBoundingClientRect();
      const p = e.touches ? e.touches[0] : e;
      return { x: (p.clientX - r.left) * (canvas.width / r.width), y: (p.clientY - r.top) * (canvas.height / r.height) };
    };
    const start = (e) => { e.preventDefault(); drawing = true; const { x, y } = pos(e); ctx.beginPath(); ctx.moveTo(x, y); };
    const move = (e) => { if (!drawing) return; e.preventDefault(); const { x, y } = pos(e); ctx.lineTo(x, y); ctx.stroke(); };
    const end = () => { drawing = false; };
    const evs = [['mousedown', start], ['mousemove', move], ['mouseup', end], ['mouseleave', end], ['touchstart', start], ['touchmove', move], ['touchend', end]];
    evs.forEach(([n, f]) => canvas.addEventListener(n, f, { passive: false }));
    return () => evs.forEach(([n, f]) => canvas.removeEventListener(n, f));
  }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium">{label}</span>
        <button type="button" onClick={clear} className="text-[10px] text-rose-600 hover:underline inline-flex items-center gap-1" data-testid={`${testId}-clear`}>
          <Eraser className="h-3 w-3" /> Clear
        </button>
      </div>
      <canvas ref={canvasRef} width={480} height={160} className="w-full border-2 border-dashed border-slate-300 rounded bg-white cursor-crosshair touch-none" data-testid={testId} />
    </div>
  );
});
SignaturePad.displayName = 'SignaturePad';
